import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CalendarDays, ChevronRight, RefreshCw } from 'lucide-react'
import exmclExamService, { type ExmclExamDefinition } from '@/services/exmclExamService'
import {
  Badge,
  Button,
  Card,
  CardToolbar,
  DataTable,
  EmptyState,
  FilterBar,
  Page,
  type DataTableColumn,
} from '@/components/ui'
import { errorMessage } from '@/components/exmcl/ExmclUi'
import '@/components/exmcl/exmcl.css'

type ExamRow = ExmclExamDefinition & {
  scheduleStatus?: 'draft' | 'published'
  scheduleStartDate?: string | null
  scheduleEndDate?: string | null
}

const scheduleLabel = (row: ExamRow) => {
  if (row.scheduleStatus === 'published') return 'Published'
  if (row.scheduleStartDate && row.scheduleEndDate) return 'Draft'
  return 'Not started'
}

const ExmclDatesheets: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  const [items, setItems] = useState<ExamRow[]>([])
  const [loading, setLoading] = useState(true)

  const setSearchQuery = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value.trim()) next.set('q', value)
    else next.delete('q')
    setSearchParams(next, { replace: true })
  }

  const load = async () => {
    setLoading(true)
    try {
      setItems(await exmclExamService.getAll())
    } catch (error) {
      toast.error(errorMessage(error, 'Exams didn’t load.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return items
    return items.filter((item) => [item.name, item.code].some((v) => String(v || '').toLowerCase().includes(needle)))
  }, [items, search])

  const columns: DataTableColumn<ExamRow>[] = [
    {
      key: 'exam',
      header: 'Exam',
      render: (row) => (
        <span>
          <strong>{row.code}</strong> — {row.name}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Date sheet',
      render: (row) => {
        const label = scheduleLabel(row)
        const tone = label === 'Published' ? 'success' : label === 'Draft' ? 'warning' : 'neutral'
        return <Badge tone={tone}>{label}</Badge>
      },
    },
    {
      key: 'window',
      header: 'Window',
      render: (row) =>
        row.scheduleStartDate && row.scheduleEndDate
          ? `${row.scheduleStartDate} → ${row.scheduleEndDate}`
          : '—',
    },
    {
      key: 'action',
      header: '',
      align: 'right',
      interactive: true,
      render: (row) => (
        <Button size="sm" icon={ChevronRight} onClick={() => navigate(`/exmcl/datesheets/${row._id}`)}>
          Open
        </Button>
      ),
    },
  ]

  return (
    <Page>
      <Card flush title="Exam date sheets" subtitle="One schedule per exam — all classes on the same grid">
        <CardToolbar>
          <FilterBar
            search={{
              placeholder: 'Search exam name or code',
              value: search,
              onChange: setSearchQuery,
              label: 'Search exams',
            }}
            actions={
              <Button icon={RefreshCw} onClick={() => void load()} loading={loading && items.length > 0}>
                Refresh
              </Button>
            }
          />
        </CardToolbar>
        <DataTable
          caption="Exams"
          columns={columns}
          rows={rows}
          getRowKey={(row) => row._id}
          onRowClick={(row) => navigate(`/exmcl/datesheets/${row._id}`)}
          loading={loading}
          loadingLabel="Loading exams…"
          empty={
            items.length === 0 ? (
              <EmptyState
                icon={CalendarDays}
                title="No exams this session"
                body="Add exams under Exams, set subjects on the matrix, then build a date sheet here."
              />
            ) : (
              <EmptyState compact icon={CalendarDays} title="No exams match this search" />
            )
          }
        />
      </Card>
    </Page>
  )
}

export default ExmclDatesheets
